import React, { useRef, useState, useMemo, useCallback } from 'react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    InteractionMode
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import zoomPlugin from 'chartjs-plugin-zoom'
import 'chartjs-adapter-date-fns'

// Chart.js 등록
ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
)

// 생체신호 데이터 인터페이스
export interface IBiometricChartItem {
    x: number | string  // 숫자 또는 ISO 8601 문자열 지원
    y: number
    yMin?: number
    yMax?: number
}

interface IBiometricsChartProps {
    className?: string
    heartRateData?: IBiometricChartItem[]
    respirationData?: IBiometricChartItem[]
    fallData?: IBiometricChartItem[]
    realtimeHeartRate?: IBiometricChartItem[]
    realtimeRespiration?: IBiometricChartItem[]
    realtimeFallData?: IBiometricChartItem[]
    height?: number
    title?: string
}

const BiometricsChart: React.FC<IBiometricsChartProps> = ({
    className,
    heartRateData,
    respirationData,
    fallData,
    realtimeHeartRate,
    realtimeRespiration,
    realtimeFallData,
    height = 320,
    title
}) => {
    const { t } = useTranslation()
    
    // 차트 참조들
    const heartRateChartRef = useRef<ChartJS<'line'>>(null)
    const respirationChartRef = useRef<ChartJS<'line'>>(null)
    const fallChartRef = useRef<ChartJS<'line'>>(null)

    // 데이터 모드 상태
    const [viewMode, setViewMode] = useState<'daily' | 'realtime'>('realtime')
    
    // 줌/팬 상태 (모든 차트 동기화용)
    const [syncedZoom, setSyncedZoom] = useState<{
        min?: number
        max?: number
    }>({})

    // 데이터 선택 및 변환 로직
    const processedData = useMemo(() => {
        // 시간 값을 timestamp로 변환하는 헬퍼 함수
        const parseTimestamp = (x: number | string): number => {
            if (typeof x === 'string') {
                return new Date(x).getTime()
            }
            return x > 1000000000000 ? x : x * 1000 // 이미 밀리초 단위인지 확인
        }

        // 낙상 데이터 전용 처리 함수 - 연속적인 낙상 이벤트를 그룹화
        const selectFallData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            const filteredData = sourceData.filter(item => {
                const timestamp = parseTimestamp(item.x)
                return !isNaN(timestamp) && timestamp > 0 && 
                       item.y != null && !isNaN(item.y)
            }).sort((a, b) => parseTimestamp(a.x) - parseTimestamp(b.x))

            if (filteredData.length === 0) return []

            // 연속적인 낙상 이벤트를 그룹화 (10초 이내의 연속 이벤트는 하나로 처리)
            const groupedFalls: { x: number, y: number, yMin?: number, yMax?: number }[] = []
            const GROUPING_THRESHOLD = 10000 // 10초 (밀리초 단위)
            
            let currentGroup: IBiometricChartItem[] = []
            
            for (let i = 0; i < filteredData.length; i++) {
                const currentItem = filteredData[i]
                const currentTimestamp = parseTimestamp(currentItem.x)
                
                if (currentGroup.length === 0) {
                    // 첫 번째 아이템이면 그룹에 추가
                    currentGroup.push(currentItem)
                } else {
                    const lastTimestamp = parseTimestamp(currentGroup[currentGroup.length - 1].x)
                    const timeDiff = currentTimestamp - lastTimestamp
                    
                    if (timeDiff <= GROUPING_THRESHOLD) {
                        // 10초 이내면 같은 그룹에 추가
                        currentGroup.push(currentItem)
                    } else {
                        // 10초 초과면 이전 그룹을 완료하고 새 그룹 시작
                        if (currentGroup.length > 0) {
                            // 그룹의 첫 번째 시간을 대표 시간으로 사용하고, y값은 합계
                            const groupSum = currentGroup.reduce((sum, item) => sum + (item.y || 0), 0)
                            groupedFalls.push({
                                x: parseTimestamp(currentGroup[0].x),
                                y: groupSum,
                                yMin: currentGroup[0].yMin,
                                yMax: currentGroup[0].yMax
                            })
                        }
                        currentGroup = [currentItem]
                    }
                }
            }
            
            // 마지막 그룹 처리
            if (currentGroup.length > 0) {
                const groupSum = currentGroup.reduce((sum, item) => sum + (item.y || 0), 0)
                groupedFalls.push({
                    x: parseTimestamp(currentGroup[0].x),
                    y: groupSum,
                    yMin: currentGroup[0].yMin,
                    yMax: currentGroup[0].yMax
                })
            }

            return groupedFalls
        }

        // 심박수와 호흡수는 min/max/avg 분리
        const selectVitalData = (dailyData?: IBiometricChartItem[], realtimeData?: IBiometricChartItem[]) => {
            const sourceData = viewMode === 'realtime' ? (realtimeData || []) : (dailyData || [])
            const filteredData = sourceData.filter(item => {
                const timestamp = parseTimestamp(item.x)
                return !isNaN(timestamp) && timestamp > 0 && 
                       item.y != null && !isNaN(item.y)
            }).sort((a, b) => parseTimestamp(a.x) - parseTimestamp(b.x))

            // 각 데이터 포인트에서 min/avg/max 값들을 논리적 순서로 정렬
            const normalizedData = filteredData.map(item => {
                let min = item.yMin ?? item.y;
                let avg = item.y;
                let max = item.yMax ?? item.y;
                
                // NaN 값 처리
                if (isNaN(min)) min = avg;
                if (isNaN(max)) max = avg;
                if (isNaN(avg)) avg = 0;
                
                // 논리적 순서 보장: min <= avg <= max
                const values = [min, avg, max].sort((a, b) => a - b);
                
                return {
                    x: parseTimestamp(item.x),
                    sortedMin: values[0],
                    sortedAvg: values[1], 
                    sortedMax: values[2]
                };
            });

            return {
                max: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedMax
                })),
                avg: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedAvg
                })),
                min: normalizedData.map(item => ({
                    x: item.x,
                    y: item.sortedMin
                }))
            }
        }

        return {
            heartRate: selectVitalData(heartRateData, realtimeHeartRate),
            respiration: selectVitalData(respirationData, realtimeRespiration),
            falls: selectFallData(fallData, realtimeFallData)
        }
    }, [
        heartRateData, respirationData, fallData,
        realtimeHeartRate, realtimeRespiration, realtimeFallData,
        viewMode
    ])

    // 차트 동기화 함수
    const syncAllCharts = useCallback((newZoom: { min?: number, max?: number }) => {
        setSyncedZoom(newZoom)
        
        const charts = [
            heartRateChartRef.current,
            respirationChartRef.current,
            fallChartRef.current
        ]

        charts.forEach(chart => {
            if (chart && chart.options.scales?.x) {
                chart.options.scales.x.min = newZoom.min
                chart.options.scales.x.max = newZoom.max
                chart.update('none')
            }
        })
    }, [])

    // 공통 차트 옵션 생성 함수 - 모바일 최적화
    const createChartOptions = useCallback((
        title: string, 
        color: string, 
        yAxisTitle: string,
        suggestedMin?: number,
        suggestedMax?: number
    ) => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index' as InteractionMode,
            intersect: false,
        },
        plugins: {
            title: {
                display: true,
                text: title,
                color: color,
                font: {
                    size: 14,
                    weight: 'bold' as const
                }
            },
            legend: {
                display: true, // 모든 차트에서 범례 표시
                position: 'top' as const,
                labels: {
                    usePointStyle: true,
                    padding: 8,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                mode: 'index' as InteractionMode,
                intersect: false,
                callbacks: {
                    title: (tooltipItems: any[]) => {
                        if (tooltipItems.length > 0) {
                            const date = new Date(tooltipItems[0].parsed.x)
                            return viewMode === 'realtime' 
                                ? format(date, 'HH:mm:ss')
                                : format(date, 'yyyy/MM/dd')
                        }
                        return ''
                    },
                    label: (context: any) => {
                        return `${yAxisTitle}: ${context.parsed.y}`
                    }
                }
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x' as const,
                    onPan: (context: any) => {
                        const { min, max } = context.chart.scales.x
                        syncAllCharts({ min, max })
                    }
                },
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x' as const,
                    onZoom: (context: any) => {
                        const { min, max } = context.chart.scales.x
                        syncAllCharts({ min, max })
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'time' as const,
                time: {
                    displayFormats: {
                        minute: viewMode === 'realtime' ? 'HH:mm' : 'MM/dd',
                        hour: viewMode === 'realtime' ? 'HH:mm' : 'MM/dd',
                        day: 'MM/dd'
                    }
                },
                title: {
                    display: true,
                    text: viewMode === 'realtime' ? '시간' : '날짜'
                },
                min: syncedZoom.min,
                max: syncedZoom.max
            },
            y: {
                title: {
                    display: true,
                    text: yAxisTitle
                },
                suggestedMin,
                suggestedMax
            }
        }
    }), [syncedZoom, viewMode, syncAllCharts])

    // 심박수/호흡수용 3라인 차트 데이터 생성 함수
    const createVitalChartData = useCallback((
        data: { max: { x: number, y: number }[], avg: { x: number, y: number }[], min: { x: number, y: number }[] }, 
        baseColor: string, 
        label: string
    ) => ({
        datasets: [
            {
                label: `${label} 최대`,
                data: data.max,
                borderColor: baseColor,
                backgroundColor: baseColor + '20',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            },
            {
                label: `${label} 평균`,
                data: data.avg,
                borderColor: baseColor + 'CC', // 약간 투명하게
                backgroundColor: baseColor + '15',
                borderWidth: 3, // 두껍게
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            },
            {
                label: `${label} 최소`,
                data: data.min,
                borderColor: baseColor + '99', // 더 투명하게
                backgroundColor: baseColor + '10',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            }
        ]
    }), [])

    // 일반 차트 데이터 생성 함수 (낙상)
    const createChartData = useCallback((data: { x: number, y: number }[], color: string, label: string) => ({
        datasets: [
            {
                label,
                data,
                borderColor: color,
                backgroundColor: color + '20',
                borderWidth: 2,
                fill: false,
                tension: 0.1,
                pointRadius: 1,
                pointHoverRadius: 4
            }
        ]
    }), [])

    // 통계 계산
    const statistics = useMemo(() => {
        const calcVitalStats = (data: { max: { x: number, y: number }[], avg: { x: number, y: number }[], min: { x: number, y: number }[] }) => {
            if (data.avg.length === 0) return null
            const avgValues = data.avg.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (avgValues.length === 0) return null
            
            return {
                latest: avgValues[avgValues.length - 1] || 0,
                average: avgValues.reduce((sum, v) => sum + v, 0) / avgValues.length,
                max: Math.max(...data.max.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))),
                min: Math.min(...data.min.map(d => d.y).filter(v => !isNaN(v) && isFinite(v)))
            }
        }

        // 낙상 전용 통계 계산 - 그룹화된 데이터의 특성을 고려
        const calcFallStats = (data: { x: number, y: number }[]) => {
            if (data.length === 0) return null
            const values = data.map(d => d.y).filter(v => !isNaN(v) && isFinite(v))
            if (values.length === 0) return null
            
            return {
                totalCount: values.reduce((sum, v) => sum + v, 0), // 전체 낙상 횟수 (모든 그룹의 합계)
                groupCount: data.length, // 그룹화된 낙상 이벤트 수
                latest: values[values.length - 1] || 0, // 최근 그룹의 낙상 횟수
                max: Math.max(...values), // 단일 그룹에서 최대 낙상 횟수
                min: Math.min(...values)  // 단일 그룹에서 최소 낙상 횟수
            }
        }

        return {
            heartRate: calcVitalStats(processedData.heartRate),
            respiration: calcVitalStats(processedData.respiration),
            falls: calcFallStats(processedData.falls)
        }
    }, [processedData])

    // 줌 리셋 함수
    const resetZoom = useCallback(() => {
        setSyncedZoom({})
        const charts = [
            heartRateChartRef.current,
            respirationChartRef.current,
            fallChartRef.current
        ]

        charts.forEach(chart => {
            if (chart && (chart as any).resetZoom) {
                (chart as any).resetZoom()
            }
        })
    }, [])

    return (
        <div className={`${className} biometric-dashboard`}>
            {/* 헤더 - 모바일 최적화 */}
            <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl">📊</span>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">{title}</h2>
                            <p className="text-sm text-gray-600">{t('V3.biometricDashboard.description')}</p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        {/* 데이터 타입 선택 탭 - 모바일 최적화 */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('realtime')}
                                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                    viewMode === 'realtime' 
                                        ? 'bg-blue-500 text-white shadow-sm' 
                                        : 'text-gray-600 hover:text-blue-600'
                                }`}
                            >
                                {t('common.realtimeView')}
                            </button>
                            <button
                                onClick={() => setViewMode('daily')}
                                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                    viewMode === 'daily' 
                                        ? 'bg-blue-500 text-white shadow-sm' 
                                        : 'text-gray-600 hover:text-blue-600'
                                }`}
                            >
                                {t('common.dayView')}
                            </button>
                        </div>

                        {/* 줌 리셋 버튼 - 모바일 최적화 */}
                        <button
                            onClick={resetZoom}
                            className="w-full px-3 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        >
                            🔍 {t('common.zoomOut')}
                        </button>
                    </div>
                </div>

                {/* 통계 카드 - 모바일 최적화 */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                    {/* 심박수 */}
                    <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                        <div className="flex items-center gap-1 mb-2">
                            <span className="text-red-500 text-sm">❤️</span>
                            <h3 className="font-semibold text-red-700 text-xs">{t('dashboard.notification.heartRate')}</h3>
                        </div>
                        {statistics.heartRate && (
                            <div className="space-y-1 text-xs">
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.current')}:</span> {Math.round(statistics.heartRate.latest)} bpm</p>
                                <p><span className="font-medium">{t('users.detail.averageTitle')}:</span> {Math.round(statistics.heartRate.average)} bpm</p>
                            </div>
                        )}
                    </div>

                    {/* 호흡수 */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                        <div className="flex items-center gap-1 mb-2">
                            <span className="text-blue-500 text-sm">🫁</span>
                            <h3 className="font-semibold text-blue-700 text-xs">{t('dashboard.notification.respiration')}</h3>
                        </div>
                        {statistics.respiration && (
                            <div className="space-y-1 text-xs">
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.current')}:</span> {Math.round(statistics.respiration.latest)} rpm</p>
                                <p><span className="font-medium">{t('users.detail.averageTitle')}:</span> {Math.round(statistics.respiration.average)} rpm</p>
                            </div>
                        )}
                    </div>

                    {/* 낙상 */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded">
                        <div className="flex items-center gap-1 mb-2">
                            <span className="text-yellow-500 text-sm">⚠️</span>
                            <h3 className="font-semibold text-yellow-700 text-xs">{t('users.detail.falldetection.title').replace(/<[^>]*>/g, '')}</h3>
                        </div>
                        {statistics.falls && (
                            <div className="space-y-1 text-xs">
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.total')}:</span> {statistics.falls.totalCount} {t('V3.biometricDashboard.stats.cases')}</p>
                                <p><span className="font-medium">{t('V3.biometricDashboard.stats.events')}:</span> {statistics.falls.groupCount} {t('V3.biometricDashboard.stats.cases')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 동기화된 차트들 - 모바일 최적화 */}
            <div className="grid grid-cols-1 gap-4">
                {/* 심박수 차트 */}
                <div className="bg-white rounded-lg shadow-lg p-3">
                    <div style={{ height: Math.max(250, height * 0.8) }}>
                        <Line
                            ref={heartRateChartRef}
                            data={createVitalChartData(processedData.heartRate, '#ef4444', t('dashboard.notification.heartRate'))}
                            options={createChartOptions(`❤️ ${t('V3.biometricDashboard.chart.heartRate.title')}`, '#ef4444', `${t('dashboard.notification.heartRate')} (bpm)`, 40, 160)}
                        />
                    </div>
                </div>

                {/* 호흡수 차트 */}
                <div className="bg-white rounded-lg shadow-lg p-3">
                    <div style={{ height: Math.max(250, height * 0.8) }}>
                        <Line
                            ref={respirationChartRef}
                            data={createVitalChartData(processedData.respiration, '#3b82f6', t('dashboard.notification.respiration'))}
                            options={createChartOptions(`🫁 ${t('V3.biometricDashboard.chart.respiration.title')}`, '#3b82f6', `${t('dashboard.notification.respiration')} (rpm)`, 8, 30)}
                        />
                    </div>
                </div>

                {/* 낙상 감지 차트 */}
                <div className="bg-white rounded-lg shadow-lg p-3">
                    <div style={{ height: Math.max(250, height * 0.8) }}>
                        <Line
                            ref={fallChartRef}
                            data={createChartData(processedData.falls, '#f59e0b', t('users.detail.falldetection.title').replace(/<[^>]*>/g, ''))}
                            options={createChartOptions(`⚠️ ${t('users.detail.falldetection.title').replace(/<[^>]*>/g, '')}`, '#f59e0b', t('users.detail.falldetection.title').replace(/<[^>]*>/g, ''), 0)}
                        />
                    </div>
                </div>
            </div>

            {/* 사용법 안내 - 모바일 최적화 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <h4 className="font-semibold text-blue-800 mb-2 text-sm">📌 차트 사용법</h4>
                <div className="space-y-1 text-xs text-blue-700">
                    <p>• <strong>동기화:</strong> 모든 차트가 시간축으로 함께 움직입니다</p>
                    <p>• <strong>확대/축소:</strong> 핀치 줌 또는 마우스 휠 사용</p>
                    <p>• <strong>이동:</strong> 차트를 드래그하여 시간대 이동</p>
                    <p>• <strong>리셋:</strong> '줌 리셋' 버튼으로 원래 보기 복원</p>
                </div>
            </div>
        </div>
    )
}

export default BiometricsChart 